import { ComponentType, useId } from 'react';

import { twMerge } from 'tailwind-merge';
import { LinkOptions, useLocation, useRouter } from '@tanstack/react-router';
import { Typography } from '../../ui';
import { NotificationCount } from '../../atoms/NotificationCount/notificationCount';
import { NotificationCountStyleVariants } from '../../atoms/NotificationCount/variants';
import { Tab, TabList, Tabs } from 'react-aria-components';

type Props = {
  links: Array<SubnavItemProps>;
  className?: string;
} & Omit<ComponentType<typeof Tabs>, 'className'>;

export function Subnav(props: Props) {
  const id = useId();
  const { links, className, ...rest } = props;
  const { pathname } = useLocation();

  return (
    <Tabs
      selectedKey={pathname}
      className={twMerge('flex flex-col bg-grayscale50 px-32 py-12', className)}
      {...rest}
    >
      <TabList className="flex space-x-12" aria-label="Sub Navigation">
        {links.map((item, index) => (
          <SubnavItem {...item} key={`subnav-${id}-item-${index}`} />
        ))}
      </TabList>
    </Tabs>
  );
}

type SubnavItemProps = {
  label: string;
  notification?: {
    count: number;
    intent: NotificationCountStyleVariants['intent'];
  };
} & LinkOptions;

function SubnavItem(props: SubnavItemProps) {
  const { label, notification, ...rest } = props;
  const { pathname } = useLocation();
  const router = useRouter();
  const resolvedLink = router.buildLocation({
    to: props.to,
    params: props.params,
  });

  const isActive = pathname === resolvedLink.href;

  return (
    <Tab
      id={rest.to}
      href={rest.to}
      className={twMerge(
        'transition-colors duration-300 ease-out outline-none rounded-4',
        isActive && 'text-primaryMain bg-grayscale0',
        !isActive && "data-[focus-visible='true']:bg-grayscale100",
      )}
    >
      <div className="px-16 py-8 flex flex-row items-center space-x-8">
        <Typography variant="body-xs" weight="medium">
          {label}
        </Typography>

        {notification && <NotificationCount {...notification} />}
      </div>
    </Tab>
  );
}
